// 계정 상태(브랜드 빌드 전용). 백엔드(/api/v1/auth·/me)에 연결. 익명 빌드에는 쓰이지 않는다.
import { defineStore } from 'pinia'
import { api, setTokens, hasSession } from '../lib/api'
import { backup as syncBackup, restore as syncRestore, lastBackup as syncLastBackup } from '../lib/sync'

export interface Me {
  id: string; email_hint: string | null; provider: string; role: string
  locale: string; country: string; status: string; plan: string; deletion_due_at: string | null
}

// 복구 키(12단어): 동기화 E2E 암호화 키의 출발점. 운영자도 못 연다 → 잃으면 복구 불가(accounts.md).
const WORDS = ['구름', '연필', '바다', '사과', '등대', '여름', '시계', '나무', '별빛', '우산', '모래', '호수', '단추', '바람', '종이', '노을', '서리', '고래', '달빛', '언덕']
export function makeRecoveryKey(): string[] {
  const a = new Uint32Array(12); crypto.getRandomValues(a)
  return Array.from(a, n => WORDS[n % WORDS.length])
}

export const useAccountStore = defineStore('account', {
  state: () => ({ user: null as Me | null, loaded: false, busy: false, recoveryKey: null as string | null }),
  getters: {
    signedIn: (s) => !!s.user,
    pendingDeletion: (s) => s.user?.status === 'pending_deletion',
  },
  actions: {
    async init() {
      if (!hasSession()) { this.loaded = true; return }
      try { this.user = await api<Me>('GET', '/me') } catch { setTokens(null, null) } finally { this.loaded = true }
    },
    // 이메일 매직링크: 코드 보내기(dev는 dev_code를 돌려줌)
    async startEmail(email: string) { return api<{ sent: boolean; email_hint: string; dev_code?: string }>('POST', '/auth/email/start', { email }, false) },
    // 코드 확인 → 로그인/가입. 가입이면 consents·age_confirmed 필요
    async verifyEmail(p: { email: string; code: string; age_confirmed?: boolean; consents?: { kind: string; granted: boolean }[]; locale?: string }) {
      const d = await api<{ access_token: string; refresh_token: string; user: Me; is_new: boolean }>('POST', '/auth/email/verify', { platform: 'web', ...p }, false)
      setTokens(d.access_token, d.refresh_token); this.user = d.user
      return d
    },
    // 애플/구글 로그인: 클라이언트가 포털에서 받은 id_token 을 보내 검증·가입/로그인.
    //  포털(App ID/Service ID·키, Google OAuth 클라이언트) 설정 전엔 서버가 501(준비중)로 답한다.
    async oauthLogin(provider: 'apple' | 'google', p: { id_token: string; age_confirmed?: boolean; consents?: { kind: string; granted: boolean }[]; locale?: string }) {
      const d = await api<{ access_token: string; refresh_token: string; user: Me; is_new: boolean }>('POST', `/auth/${provider}`, { platform: 'web', ...p }, false)
      setTokens(d.access_token, d.refresh_token); this.user = d.user
      return d
    },
    async logout() { try { await api('POST', '/auth/logout') } catch { /* ignore */ } setTokens(null, null); this.user = null; this.recoveryKey = null },
    setRecoveryKey(words: string[]) { this.recoveryKey = words.join(' ') },
    // 동기화: 복구 키로 금고 스냅샷 암호화 백업 / 복원(서버는 못 엶)
    async backup(key?: string) { const k = key || this.recoveryKey; if (!k) throw new Error('need_key'); const r = await syncBackup(k); this.recoveryKey = k; return r },
    async restore(key: string) { const r = await syncRestore(key); this.recoveryKey = key; return r },
    lastBackup() { return syncLastBackup() },
    async reauth() { return (await api<{ reauth_token: string }>('POST', '/me/reauth')).reauth_token },
    async deleteAccount(reauth_token: string, reason_codes: string[], reason_text?: string) {
      const d = await api<{ deletion_due_at: string }>('DELETE', '/me', { reauth_token, reason_codes, reason_text })
      if (this.user) { this.user.status = 'pending_deletion'; this.user.deletion_due_at = d.deletion_due_at }
      return d
    },
    async cancelDeletion() { await api('POST', '/me/deletion/cancel'); if (this.user) { this.user.status = 'active'; this.user.deletion_due_at = null } },
    async setContribute(v: boolean) { await api('PATCH', '/me', { consents: [{ kind: 'contribute', granted: v }] }) },
  },
})
